// Survey content (Bulgarian + English) for the customer feedback survey.
//
// The option `value` codes MUST match the backend's survey.constants.ts exactly
// — only these stable codes are sent to the API. Question labels are localized
// here. This is kept as a structured data module (rather than flat i18n keys)
// because a survey definition is data, not scattered marketing copy.

// Must match SURVEY_KEY_ZDRAVETO_FEEDBACK in the backend.
export const SURVEY_KEY = "zdraveto_feedback_2026";

// Required question ids (validated on the frontend AND backend).
export const REQUIRED_QUESTIONS = [
  "hospitalVisitCount",
  "lastVisit",
  "anxietyDuringVisit",
  "anxietyNow",
  "hospitalSupport",
  "satisfactionVisits",
  "satisfactionMomfidentAccess",
];

export const MAX_DESIRED_FEATURES = 2;

const bg = {
  metaTitle: "Анкета | Momfident",
  pageTitle: "Помогнете ни да направим Momfident още по-полезен!",
  pageSubtitle: "Вашето мнение е от голямо значение за нас!",
  introTime: "Време за попълване: 5 минути.",
  requiredNote: "* Задължителен въпрос",
  otherPlaceholder: "Друго...",
  submit: "Изпрати",
  submitting: "Изпращане...",
  errorRequired: "Моля, отговорете на този въпрос.",
  errorOther: "Моля, опишете вашия отговор.",
  errorSubmit: "Възникна грешка. Моля, опитайте отново.",
  linkInvalid:
    "Този линк изглежда невалиден или непълен. Моля, отворете анкетата от връзката, която получихте по имейл.",
  thankYouTitle: "Благодарим Ви за Вашата обратна връзка!",
  thankYouBody:
    "Високо оценяваме Вашата ангажираност! С попълването на анкетата Вие допринасяте за по-добра грижа и подкрепа на майките в България.",
  sections: [
    {
      intro: null,
      questions: [
        {
          id: "ayaSupport",
          type: "single",
          required: false,
          label: "Как оценявате подкрепата от психологическия асистент Ая в приложението Momfident?",
          options: [
            { value: "very_helpful", label: "Много полезна – надмина очакванията ми" },
            { value: "sufficient", label: "Достатъчна – отговори на очакванията ми" },
            { value: "insufficient", label: "Недостатъчна – не отговори на очакванията ми" },
            { value: "cannot_judge", label: "Не мога да преценя" },
          ],
        },
        {
          id: "recommend",
          type: "single",
          required: false,
          label: "Бихте ли препоръчали Momfident на приятел?",
          options: [
            { value: "yes", label: "Да" },
            { value: "no", label: "Не" },
            { value: "not_sure", label: "Не съм сигурна" },
          ],
        },
        {
          id: "desiredFeatures",
          type: "multi",
          required: false,
          maxSelect: MAX_DESIRED_FEATURES,
          allowOther: true,
          label:
            "Какви нови функционалности бихте искали да получавате в бъдеще в приложението Momfident?",
          help: "Моля, изберете до 2 най-желани отговора!",
          options: [
            { value: "psych_tests", label: "Психологични тестове, с които да разбера повече за себе си" },
            { value: "personal_specialist_chat", label: "Личен чат с подходящ доверен специалист спрямо въпроса ми" },
            { value: "none", label: "Никое от посочените" },
            { value: "emotion_journal", label: "Водене на дневник за проследяване на емоциите ми" },
            { value: "anonymous_chat", label: "Анонимен чат с други майки, които преминават през подобни предизвикателства" },
            { value: "voice_aya", label: "Гласова комуникация с Ая" },
            { value: "reliable_topics", label: "Нови теми с надеждна информация на разговор" },
          ],
        },
      ],
    },
    {
      intro: {
        title: "Грижата за детето често е свързана с много емоции...",
        body: 'В тази част бихме искали да научим повече за Вашия опит като родител в болница „Здравето“.',
      },
      questions: [
        {
          id: "hospitalVisitCount",
          type: "single",
          required: true,
          label:
            'Колко пъти досега сте посещавали болница "Здравето" в ролята си на родител през последните 12 месеца?',
          options: [
            { value: "once", label: "Само веднъж" },
            { value: "two_three", label: "2–3 пъти" },
            { value: "four_plus", label: "4 или повече пъти" },
            { value: "not_sure", label: "Не съм сигурна / Не помня" },
          ],
        },
        {
          id: "lastVisit",
          type: "single",
          required: true,
          label: "Кога беше Вашето последно посещение в болницата?",
          options: [
            { value: "still_in_hospital", label: "Все още съм в болницата" },
            { value: "last_week", label: "През последната седмица" },
            { value: "last_month", label: "През последния месец" },
            { value: "more_than_month", label: "Преди повече от месец" },
            { value: "not_sure", label: "Не помня / Не съм сигурна" },
          ],
        },
        {
          id: "anxietyDuringVisit",
          type: "scale",
          required: true,
          min: 0,
          max: 10,
          label:
            'По скалата от 0 до 10 колко тревожни се чувствахте, когато се наложи да посетите болница "Здравето"?',
          lowLabel: "Изобщо не се чувствах тревожна",
          highLabel: "Чувствах се изключително тревожна",
        },
        {
          id: "anxietyNow",
          type: "scale",
          required: true,
          min: 0,
          max: 10,
          label:
            "По скалата от 0 до 10 колко тревожни се чувствате сега?",
          lowLabel: "Изобщо не се чувствам тревожна",
          highLabel: "Чувствам се изключително тревожна",
        },
        {
          id: "hospitalSupport",
          type: "single",
          required: true,
          label:
            'Как оценявате подкрепата, която получихте от болница „Здравето“ за Вас и Вашето дете?',
          options: [
            { value: "very_helpful", label: "Много полезна – надмина очакванията ми" },
            { value: "sufficient", label: "Достатъчна – отговори на очакванията ми" },
            { value: "insufficient", label: "Недостатъчна – не отговори на очакванията ми" },
            { value: "cannot_judge", label: "Не мога да преценя" },
          ],
        },
        {
          id: "satisfactionVisits",
          type: "scale",
          required: true,
          min: 1,
          max: 5,
          label:
            'Изобщо, доколко сте удовлетворени от посещенията си в болница "Здравето"?',
        },
        {
          id: "satisfactionMomfidentAccess",
          type: "scale",
          required: true,
          min: 1,
          max: 5,
          label:
            "Доколко сте удовлетворени, че болница Здравето Ви е дала достъп до приложението Momfident?",
        },
      ],
    },
  ],
};

const en = {
  metaTitle: "Survey | Momfident",
  pageTitle: "Help us make Momfident even more useful!",
  pageSubtitle: "Your opinion matters a lot to us!",
  introTime: "Time to complete: 5 minutes.",
  requiredNote: "* Required question",
  otherPlaceholder: "Other...",
  submit: "Submit",
  submitting: "Submitting...",
  errorRequired: "Please answer this question.",
  errorOther: "Please describe your answer.",
  errorSubmit: "Something went wrong. Please try again.",
  linkInvalid:
    "This link looks invalid or incomplete. Please open the survey from the link you received by email.",
  thankYouTitle: "Thank you for your feedback!",
  thankYouBody:
    "We highly value your engagement! By completing the survey you contribute to better care and support for mothers in Bulgaria.",
  sections: [
    {
      intro: null,
      questions: [
        {
          id: "ayaSupport",
          type: "single",
          required: false,
          label: "How would you rate the support from the psychological assistant Aya in the Momfident app?",
          options: [
            { value: "very_helpful", label: "Very helpful – exceeded my expectations" },
            { value: "sufficient", label: "Sufficient – met my expectations" },
            { value: "insufficient", label: "Insufficient – did not meet my expectations" },
            { value: "cannot_judge", label: "I can’t tell" },
          ],
        },
        {
          id: "recommend",
          type: "single",
          required: false,
          label: "Would you recommend Momfident to a friend?",
          options: [
            { value: "yes", label: "Yes" },
            { value: "no", label: "No" },
            { value: "not_sure", label: "I’m not sure" },
          ],
        },
        {
          id: "desiredFeatures",
          type: "multi",
          required: false,
          maxSelect: MAX_DESIRED_FEATURES,
          allowOther: true,
          label: "What new features would you like to have in the Momfident app in the future?",
          help: "Please choose up to 2 of your most desired answers!",
          options: [
            { value: "psych_tests", label: "Psychological tests to learn more about myself" },
            { value: "personal_specialist_chat", label: "Personal chat with a suitable trusted specialist for my question" },
            { value: "none", label: "None of the above" },
            { value: "emotion_journal", label: "Keeping a journal to track my emotions" },
            { value: "anonymous_chat", label: "Anonymous chat with other mothers going through similar challenges" },
            { value: "voice_aya", label: "Voice communication with Aya" },
            { value: "reliable_topics", label: "New conversation topics with reliable information" },
          ],
        },
      ],
    },
    {
      intro: {
        title: "Caring for a child often comes with many emotions...",
        body: "In this part we would like to learn more about your experience as a parent at “Zdraveto” hospital.",
      },
      questions: [
        {
          id: "hospitalVisitCount",
          type: "single",
          required: true,
          label: "How many times have you visited “Zdraveto” hospital as a parent in the last 12 months?",
          options: [
            { value: "once", label: "Only once" },
            { value: "two_three", label: "2–3 times" },
            { value: "four_plus", label: "4 or more times" },
            { value: "not_sure", label: "Not sure / I don’t remember" },
          ],
        },
        {
          id: "lastVisit",
          type: "single",
          required: true,
          label: "When was your last visit to the hospital?",
          options: [
            { value: "still_in_hospital", label: "I’m still in the hospital" },
            { value: "last_week", label: "In the last week" },
            { value: "last_month", label: "In the last month" },
            { value: "more_than_month", label: "More than a month ago" },
            { value: "not_sure", label: "I don’t remember / I’m not sure" },
          ],
        },
        {
          id: "anxietyDuringVisit",
          type: "scale",
          required: true,
          min: 0,
          max: 10,
          label: "On a scale from 0 to 10, how anxious did you feel when you had to visit “Zdraveto” hospital?",
          lowLabel: "Not anxious at all",
          highLabel: "I felt extremely anxious",
        },
        {
          id: "anxietyNow",
          type: "scale",
          required: true,
          min: 0,
          max: 10,
          label: "On a scale from 0 to 10, how anxious do you feel now?",
          lowLabel: "Not anxious at all",
          highLabel: "I feel extremely anxious",
        },
        {
          id: "hospitalSupport",
          type: "single",
          required: true,
          label: "How would you rate the support you received from “Zdraveto” hospital for you and your child?",
          options: [
            { value: "very_helpful", label: "Very helpful – exceeded my expectations" },
            { value: "sufficient", label: "Sufficient – met my expectations" },
            { value: "insufficient", label: "Insufficient – did not meet my expectations" },
            { value: "cannot_judge", label: "I can’t tell" },
          ],
        },
        {
          id: "satisfactionVisits",
          type: "scale",
          required: true,
          min: 1,
          max: 5,
          label: "Overall, how satisfied are you with your visits to “Zdraveto” hospital?",
        },
        {
          id: "satisfactionMomfidentAccess",
          type: "scale",
          required: true,
          min: 1,
          max: 5,
          label: "How satisfied are you that “Zdraveto” hospital gave you access to the Momfident app?",
        },
      ],
    },
  ],
};

export const surveyFeedbackContent = { bg, en };
